import { useCallback, useContext, useRef, useState } from 'react'
import { validarPalabrasProhibidas } from '../services/products.services'
import { TenantsContext } from '../../../context/tenants.context'

export interface IPalabraProhibidaMatch {
  idGrupo: number
  nombreGrupo: string
  accion: 'advertir' | 'bloquear'
  mensaje: string
  palabraList: string[]
}

export interface ErrorPP {
  mensaje: string
  error: boolean
  warning: boolean
}

export const defaultErrorTitulo: ErrorPP = {
  mensaje: '',
  error: false,
  warning: false
}

const mapPalabraProhibida = (match: IPalabraProhibidaMatch | null): ErrorPP => {
  if (!match) return defaultErrorTitulo
  return {
    mensaje: match.mensaje,
    error: match.accion === 'bloquear',
    warning: match.accion === 'advertir'
  }
}

const usePalabrasProhibidas = () => {
  const { tenant } = useContext(TenantsContext)
  const [loading, setLoading] = useState<boolean>(false)
  const [tituloProhibido, setTituloProhibido] = useState<ErrorPP>(defaultErrorTitulo)
  const ultimoValorValidado = useRef<string | null>(null)

  // limpia el resultado inmediatamente cuando el texto se aleja del último validado,
  // para no dejar el error/warning viejo trabando el formulario mientras se corrige
  const handleTituloChange = useCallback((valor: string) => {
    if ((valor || '').trim() !== ultimoValorValidado.current) {
      setTituloProhibido(defaultErrorTitulo)
    }
  }, [])

  const resetTituloProhibido = useCallback(() => {
    ultimoValorValidado.current = null
    setTituloProhibido(defaultErrorTitulo)
  }, [])

  const handlePalabrasProhibidas = useCallback(
    async (valor: string) => {
      const texto = (valor || '').trim()
      if (texto === ultimoValorValidado.current) return

      if (!texto) {
        ultimoValorValidado.current = texto
        setTituloProhibido(defaultErrorTitulo)
        return
      }

      try {
        setLoading(true)
        const res = await validarPalabrasProhibidas(tenant, texto)
        ultimoValorValidado.current = texto
        setTituloProhibido(mapPalabraProhibida(res.data.data))
      } catch (e) {
        console.log(e)
      } finally {
        setLoading(false)
      }
    },
    [tenant]
  )

  return {
    tituloProhibido,
    handlePalabrasProhibidas,
    handleTituloChange,
    resetTituloProhibido,
    loading
  }
}
export default usePalabrasProhibidas
